import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import {
  createBoatWant,
  deleteBoatWant,
  fetchBoatWantMatches,
  fetchBoatWants,
  previewBoatWant,
  updateBoatWant,
  type CreateBoatWantBody,
  type UpdateBoatWantBody,
} from './boat-want-api'
import type { BoatWant, WantCriteria, WantPreview } from '@/types/boat-want'

export const BOAT_WANTS_KEY = ['boat-wants'] as const

export const useBoatWants = (customerId: string) => {
  return useQuery({
    queryKey: ['boat-wants', { customer_id: customerId }],
    queryFn: () => fetchBoatWants(customerId),
    enabled: !!customerId,
  })
}

export const useCreateBoatWant = () => {
  const qc = useQueryClient()
  return useMutation<BoatWant, Error, CreateBoatWantBody>({
    mutationFn: createBoatWant,
    onSuccess: () => qc.invalidateQueries({ queryKey: BOAT_WANTS_KEY }),
  })
}

export const useUpdateBoatWant = () => {
  const qc = useQueryClient()
  return useMutation<BoatWant, Error, { id: string; body: UpdateBoatWantBody }>({
    mutationFn: ({ id, body }) => updateBoatWant(id, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: BOAT_WANTS_KEY }),
  })
}

export const useDeleteBoatWant = () => {
  const qc = useQueryClient()
  return useMutation<void, Error, string>({
    mutationFn: deleteBoatWant,
    onSuccess: () => qc.invalidateQueries({ queryKey: BOAT_WANTS_KEY }),
  })
}

export const useBoatWantMatches = (wantId: string | null) => {
  return useQuery({
    queryKey: ['boat-wants', 'matches', wantId],
    queryFn: () => fetchBoatWantMatches(wantId as string),
    enabled: !!wantId,
  })
}

export const usePreviewBoatWant = () => {
  return useMutation<WantPreview, Error, { raw_text: string; criteria?: WantCriteria }>({
    mutationFn: previewBoatWant,
  })
}
